import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { boardOfColumn, boardsQueryOptions, lossReasonsQueryOptions } from '@/entities/board'
import { employeesQueryOptions } from '@/entities/employee'
import { projectBySlugQueryOptions } from '@/entities/project'
import { useSession } from '@/entities/session'
import { EntityTagsEditor } from '@/entities/tag'
import { ArchiveProjectDialog, useArchiveProject } from '@/features/archive-project'
import { usePatchProject } from '@/features/edit-project-card'
import { ChangeProjectStage } from '@/features/move-project'
import { useSearchStore } from '@/features/search'
import { ApiError } from '@/shared/api'
import { SLUG_RE } from '@/shared/lib'
import { Breadcrumb } from '@/shared/ui/breadcrumb'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { Icon } from '@/shared/ui/icon'
import { InlineText } from '@/shared/ui/inline-edit'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'
import { StatusScreen } from '@/shared/ui/status-screen'
import { toast } from '@/shared/ui/toast'
import { ActivityFeed } from '@/widgets/activity-feed'
import { CommentThread } from '@/widgets/comment-thread'
import { DocumentSlots } from '@/widgets/document-slots'
import { EntityPageHeader, EntityPageLayout, type EntityPageTab, Meta } from '@/widgets/entity-page'
import { InvoiceList } from '@/widgets/invoice-list'
import { MeetingList } from '@/widgets/meeting-list'
import { TaskList } from '@/widgets/task-list'
import {
  ProjectHeaderExtra,
  ProjectMeta,
  ProjectMetaEditable,
  ProjectStageProgress,
} from './-ui/project-meta'
import { ProjectLinks, ProjectTeam } from './-ui/project-sections'

// Лента — не вкладка, а постоянный правый рельс (rail в EntityPageLayout),
// поэтому в наборе вкладок её нет.
const PROJECT_TABS = ['docs', 'invoices', 'meetings', 'tasks', 'comments', 'links', 'team'] as const
type ProjectTab = (typeof PROJECT_TABS)[number]

// Табы — состояние экрана, живут в search-param. Опционально (ссылки не тащат
// search), невалидное значение → undefined → docs по умолчанию в компоненте.
const ProjectSearchSchema = z.object({
  tab: z.enum(PROJECT_TABS).optional().catch(undefined),
})

export const Route = createFileRoute('/_app/$company/$project')({
  validateSearch: (search) => ProjectSearchSchema.parse(search),
  head: () => ({ meta: [{ title: 'Проект · STIT Deal' }] }),
  component: ProjectPage,
})

const TAB_LABELS: Record<ProjectTab, string> = {
  docs: 'Документы',
  invoices: 'Счета',
  meetings: 'Встречи',
  tasks: 'Задачи',
  comments: 'Комментарии',
  links: 'Ссылки',
  team: 'Команда',
}

function ProjectPage() {
  const { company, project } = Route.useParams()
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { session } = useSession()
  const isEmployee = session?.kind === 'employee'
  const archive = useArchiveProject()
  const [archiveOpen, setArchiveOpen] = useState(false)

  const projectQ = useQuery(projectBySlugQueryOptions(company, project))
  const boardsQ = useQuery(boardsQueryOptions)
  const lossReasonsQ = useQuery(lossReasonsQueryOptions)
  const employeesQ = useQuery(employeesQueryOptions)
  // Inline-правка заголовка/адреса/приоритета (сотрудник) точечным PATCH.
  const patchProject = usePatchProject(projectQ.data?.id ?? '')

  // Переименование slug меняет адрес — уводим на новый, старый by-slug отвалится.
  // Компания при правке не меняется, поэтому company-параметр берём текущий.
  function onSaved(newSlug: string) {
    if (newSlug !== project) {
      void navigate({
        to: '/$company/$project',
        params: { company, project: newSlug },
        search: { tab },
      })
    }
  }

  if (projectQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (projectQ.error) {
    const e = projectQ.error
    if (e instanceof ApiError && e.isForbidden) {
      return <StatusScreen title="Нет доступа" message="У вас нет доступа к этому проекту." />
    }
    if (e instanceof ApiError && e.status === 404) {
      return <StatusScreen title="Проект не найден" message="Проверьте адрес." />
    }
    return (
      <StatusScreen title="Не удалось загрузить проект" message="Попробуйте обновить страницу." />
    )
  }

  const d = projectQ.data
  // Воронка проекта выводится из его колонки — этапы берём из неё.
  const board = boardOfColumn(boardsQ.data ?? [], d.column_id)
  const column = board?.columns.find((c) => c.id === d.column_id)
  const lossReasonName =
    d.outcome?.reason_id != null
      ? lossReasonsQ.data?.find((r) => r.id === d.outcome?.reason_id)?.name
      : undefined

  // Заказчику — документы, счета (с оплатой), задачи и встречи (видимые ему) и
  // комментарии; команда и лента — контур сотрудника. Что видно заказчику,
  // решает бэкенд по пометкам, фронт срез не дублирует. Ссылки заказчик видит,
  // когда они есть (правка — сотрудника).
  const tabValues: readonly ProjectTab[] = isEmployee
    ? PROJECT_TABS
    : ([
        'docs',
        'invoices',
        'tasks',
        'meetings',
        'comments',
        ...((d.links ?? []).length > 0 ? (['links'] as const) : []),
      ] as const)
  const activeTab: ProjectTab = tab && tabValues.includes(tab) ? tab : 'docs'

  const tabs: EntityPageTab<ProjectTab>[] = tabValues.map((value) => ({
    value,
    label: TAB_LABELS[value],
    content:
      value === 'docs' ? (
        <DocumentSlots
          entityKind="project"
          entityId={d.id}
          docLink={(docId) => ({
            to: '/$company/$project/documents/$doc',
            params: { company: d.company_slug, project: d.slug, doc: docId },
          })}
        />
      ) : value === 'comments' ? (
        <CommentThread entityKind="project" entityId={d.id} />
      ) : value === 'tasks' ? (
        <TaskList
          projectId={d.id}
          isEmployee={isEmployee}
          taskLink={(taskId) => ({
            to: '/$company/$project/tasks/$task',
            params: { company: d.company_slug, project: d.slug, task: taskId },
          })}
        />
      ) : value === 'invoices' ? (
        <InvoiceList
          source={{ kind: 'project', projectId: d.id }}
          isEmployee={isEmployee}
          invoiceLink={(invoiceId) => ({
            to: '/$company/$project/invoices/$invoice',
            params: { company: d.company_slug, project: d.slug, invoice: invoiceId },
          })}
        />
      ) : value === 'links' ? (
        <ProjectLinks project={d} isEmployee={isEmployee} />
      ) : value === 'team' ? (
        <ProjectTeam project={d} employees={employeesQ.data ?? []} />
      ) : (
        <MeetingList
          scope={{ kind: 'project', id: d.id }}
          isEmployee={isEmployee}
          meetingLink={(meetingId) => ({
            to: '/$company/$project/meetings/$meeting',
            params: { company: d.company_slug, project: d.slug, meeting: meetingId },
          })}
        />
      ),
  }))

  // Статус — первая meta-ячейка «Этап» (не рядом с h1): сотруднику интерактивная
  // смена этапа, остальным — статичный бейдж/чип.
  const openBlockers = d.open_blocking_task_count ?? 0
  const statusContent =
    isEmployee && board ? (
      <span className="flex flex-wrap items-center gap-2">
        <ChangeProjectStage
          projectId={d.id}
          projectTitle={d.title}
          columnId={d.column_id}
          columns={board.columns}
          boards={(boardsQ.data ?? []).filter((b) => b.id !== board.id)}
          lossReasons={lossReasonsQ.data ?? []}
          openBlockingCount={openBlockers}
          {...(d.outcome ? { outcome: d.outcome } : {})}
          {...(column ? { currentColumn: column } : {})}
        />
        {openBlockers > 0 && (
          <button
            type="button"
            title="Открытые блокирующие задачи — к списку задач"
            onClick={() => void navigate({ search: { tab: 'tasks' } })}
            className="rounded-pill focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
          >
            <Chip tone="accent" variant="outline">
              {openBlockers} блокирует
            </Chip>
          </button>
        )}
      </span>
    ) : d.outcome ? (
      // Терминальный исход: бейдж + (заказчику) заполненная воронка.
      <span className="flex flex-wrap items-center gap-3">
        <StatusBadge status={isEmployee ? d.outcome.result : 'completed'} />
        {!isEmployee && d.stages && d.stages.length > 0 && (
          <ProjectStageProgress stages={d.stages} />
        )}
      </span>
    ) : !isEmployee && d.stages && d.stages.length > 0 ? (
      // Customer-срез: имя текущего этапа + тихая полоска прогресса воронки.
      <ProjectStageProgress stages={d.stages} />
    ) : column ? (
      <Chip tone="neutral" variant="soft">
        {column.name}
      </Chip>
    ) : null

  const header = (
    <EntityPageHeader
      breadcrumb={
        <Breadcrumb
          items={[
            // Крошка компании ведёт на её хаб и заказчику — customer-срез хаба
            // отработает сам на том же роуте.
            {
              label: d.company_name,
              to: '/companies/$company',
              params: { company: d.company_slug },
            },
            { label: d.title },
          ]}
        />
      }
      titleText={d.title}
      title={
        isEmployee ? (
          <InlineText
            value={d.title}
            ariaLabel="Название проекта"
            validate={(v) => (v.trim() ? null : 'Укажите название')}
            onSave={(v) =>
              patchProject.mutate(
                { title: v },
                { onError: () => toast.error('Не удалось сохранить') },
              )
            }
          />
        ) : (
          d.title
        )
      }
      subtitle={
        isEmployee ? (
          <span className="flex items-center gap-1 font-mono text-xs text-text-tertiary">
            /{d.company_slug}/
            <InlineText
              value={d.slug}
              ariaLabel="Адрес проекта (slug)"
              validate={(v) => (SLUG_RE.test(v) ? null : 'Латиница, цифры и дефис')}
              onSave={(v) =>
                patchProject.mutate(
                  { slug: v },
                  {
                    onSuccess: () => onSaved(v),
                    onError: (e) =>
                      toast.error(
                        e instanceof ApiError && e.code === 'SLUG_TAKEN'
                          ? 'Такой адрес уже занят'
                          : 'Не удалось сохранить',
                      ),
                  },
                )
              }
            />
          </span>
        ) : (
          <span className="font-mono text-xs text-text-tertiary">
            /{d.company_slug}/{d.slug}
          </span>
        )
      }
      {...(statusContent != null ? { status: <Meta label="Этап">{statusContent}</Meta> } : {})}
      meta={
        isEmployee ? (
          <ProjectMetaEditable project={d} employees={employeesQ.data ?? []} />
        ) : (
          <ProjectMeta project={d} onTabClick={(t) => void navigate({ search: { tab: t } })} />
        )
      }
      {...(isEmployee
        ? {
            tags: (
              <EntityTagsEditor
                entityKind="project"
                entityId={d.id}
                tags={d.tags ?? []}
                onTagClick={(tag) => useSearchStore.getState().openWithTag(tag)}
              />
            ),
          }
        : {})}
      {...(isEmployee
        ? {
            actions: d.archived ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => archive.mutate({ id: d.id, archived: false })}
              >
                <Icon name="check" size={16} />
                Вернуть из архива
              </Button>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => setArchiveOpen(true)}>
                <Icon name="collapse" size={16} />В архив
              </Button>
            ),
          }
        : {})}
      extra={<ProjectHeaderExtra project={d} {...(lossReasonName ? { lossReasonName } : {})} />}
    />
  )

  return (
    <>
      <EntityPageLayout
        header={header}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(value) => void navigate({ search: { tab: value } })}
        tabsLabel="Разделы проекта"
        {...(isEmployee
          ? { rail: <ActivityFeed source={{ kind: 'project', id: d.id }} />, railTitle: 'Лента' }
          : {})}
      />

      {archiveOpen && (
        <ArchiveProjectDialog
          project={{ id: d.id, title: d.title }}
          open
          onClose={() => setArchiveOpen(false)}
          onArchived={() => void navigate({ to: '/boards', search: {} })}
        />
      )}
    </>
  )
}
